import React, { useEffect, useState, useRef } from 'react';
import {Link, useParams} from 'react-router-dom';
import './Look.css';
import Header from './common/Header';
import httpService from '../services/HttpService';
import { formattedPrice } from '../utils/priceFormatter';
import SizeChartModal from './common/SizeChartModal';
import useIsMobile from '../hooks/useIsMobile';

function Look() {
    const { lookId } = useParams(); // Use the look ID from the route
    const [look, setLook] = useState(null);
    const effectRan = useRef(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedSize, setSelectedSize] = useState(null);
    const [activeProduct, setActiveProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const [savedItems, setSavedItems] = useState({});
    const [notification, setNotification] = useState(null); // Notification state
    const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
    const [sizeChart, setSizeChart] = useState(null);
    const [alertedStocks, setAlertedStocks] = useState(new Set());
    const [desktopModalOpen, setDesktopModalOpen] = useState(false);
    const isMobile = useIsMobile();
    const [mainImageIndex, setMainImageIndex] = useState(0);
    const headerRef = useRef();

    const setAlertedItems = () => {
        const initialAlerted = new Set(
            activeProduct?.stocks.filter((s) => s.alert_count > 0).map((s) => s.id)
        );

        setAlertedStocks(initialAlerted);
    }

    const fetchSizeChart = async () => {
        try {
            const response = await httpService.get(`/size-chart?category_id=${activeProduct.category_id}&brand_id=${look.brand_id}&product_id=${look.id}`);
            const res = await response.json();

            setSizeChart(res.table);
            setIsSizeChartOpen(true);
        } catch (error) {
            console.error("Failed to fetch size chart:", error);
        }
    };

    const handleSizeSelect = (size) => {
        setSelectedSize(size);
        handleAddProductToCart(activeProduct.id, size);
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsModalOpen(false);
            setIsClosing(false);
        }, 600);
    };

    const closeDesktopModal = () => {
        if(isSizeChartOpen) {
            setIsSizeChartOpen(false);
        } else
        setDesktopModalOpen(false)
    };

    useEffect(() => {
        setAlertedItems();
    }, [activeProduct]);

    useEffect(() => {
        const scrollbarWidth = getScrollbarWidth();

        if (isModalOpen || isSizeChartOpen) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.paddingRight = "0px";
            document.body.style.overflow = "";
        }
    }, [isModalOpen, isSizeChartOpen]);

    useEffect(() => {
        if (effectRan.current) return;
        effectRan.current = true;
        const fetchLook = async () => {
            try {
                const response = await httpService.get(`/looks/${lookId}`).then(response => response.json()); // Use HttpService
                setLook(response);
            } catch (error) {
                console.error('Error fetching look:', error);
            }
        };

        fetchLook();
    }, [lookId]);

    const toggleSave = async (product) => {
        try {
            if (product.favourite?.id || savedItems[product.id]) {
                const favouriteId = product.favourite?.id || savedItems[product.id];
                const response = await httpService.delete(`/favourites/${favouriteId}`);
                const res = await response.json();
                if (!res.message) throw new Error("Failed to remove favorite");
                product.favourite = null;
                
                setSavedItems((prev) => {
                    const updated = { ...prev };
                    delete updated[product.id];
                    return updated;
                });
    
            } else {
                const response = await httpService.post("/favourites", { product_id: product.id });
                const res = await response.json();
                if (res?.id) {
                    setSavedItems((prev) => ({
                        ...prev,
                        [product.id]: res.id,
                    }));
                } else {
                    throw new Error("Invalid response from server");
                }
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    function getScrollbarWidth() {
        const div = document.createElement("div");
        div.style.width = "100px";
        div.style.height = "100px";
        div.style.overflow = "scroll";
        document.body.appendChild(div);
        const scrollbarWidth = div.offsetWidth - div.clientWidth;
        document.body.removeChild(div);
        return scrollbarWidth;
    }

    const toggleRestock = async (stockId, size) => {
        try {
            const isAlerted = alertedStocks.has(stockId);
            const endpoint = "/restock-alerts";
            const method = isAlerted ? "delete" : "post";
            const payload = { product_id: activeProduct.id, size };

            const response = await httpService[method](endpoint, payload);
            const res = await response.json();
    
            if ((isAlerted && res?.message) || (!isAlerted && res?.id)) {
                setAlertedStocks(prev => {
                    const updated = new Set(prev);
                    isAlerted ? updated.delete(stockId) : updated.add(stockId);
                    return updated;
                });
    
                setNotification(isAlerted ? "Оповещение удалено" : "Оповещение о пополнении отправлено");
            }
        } catch (error) {
            console.error("Error:", error);
            setNotification("Ошибка");
        }
    
        setTimeout(() => setNotification(null), 1500);
    };

    const handleAddProductToCart = async (productId, size) => {
        const cartData = {
            product_id: productId,
            size,
            count: 1,
        };

        try {
            const response = await httpService
                .post('/cart', cartData);

            setIsModalOpen(false);
            setDesktopModalOpen(false);

            // Check if the response has a success property or status (adjust based on your backend response)
            if (response.success || response.status === 200 || response.status === 201) {
                window.ym && window.ym(101728239, 'reachGoal', 'add_to_cart');
                setNotification('Товар успешно добавлен в корзину');
                headerRef.current?.incrementCart();
            } else {
                throw new Error('Error adding to cart: ' + (response.message || 'Server error'));
            }
        } catch (error) {
            console.error('Error:', error);
            setNotification(`Ошибка при добавлении товара в корзину: ${error.message || 'Неизвестная ошибка'}`);
        }

        setTimeout(() => setNotification(null), 3000);
    };

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex < look.images.length - 1 ? prevIndex + 1 : 0
        );
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : look.images.length - 1
        );
    };

    function handleOverlayClick(event) {
        if (!event.target.closest('.size-chart-modal') && isSizeChartOpen) {
            event.stopPropagation();
        } else {
            setIsModalOpen(false);
        }
    }

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].clientX;
        handleSwipe();
    };

    const handleSwipe = () => {
        const swipeThreshold = 50; // Minimum distance for swipe
        if (touchStartX.current - touchEndX.current > swipeThreshold) {
            nextImage();
        } else if (touchEndX.current - touchStartX.current > swipeThreshold) {
            prevImage();
        }
    };

    if (!look) {
        // return <div>Loading...</div>;
        return <div></div>;
    }

    const shownImages = look.images;

    return (
        <div>
            <Header ref={headerRef} />
            <main className="product-main">
                <section className="product-single-section">
                    <div className="product-single-images">
                        <div style={{ width: '85%', display: 'flex' }}>
                            {shownImages.length > 1 && (
                                <div className="product-single-image-list">
                                    {shownImages
                                        .map((image, index) => (
                                            <img
                                                key={index}
                                                src={image.link}
                                                alt={`Thumbnail ${index + 1}`}
                                                onClick={() => setMainImageIndex(index)}
                                                style={{ cursor: 'pointer' }}
                                                className={index === mainImageIndex ? 'active-thumbnail' : ''}
                                            />
                                        ))
                                    }
                                </div>
                            )}
                            <div className="product-single-right-image">
                                <img src={shownImages[mainImageIndex]?.link} alt="Main img" />
                            </div>
                        </div>
                    </div>
                    <div
                        className="product-slider"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <img
                            src={look.images[currentImageIndex]?.link}
                            alt="Main Look"
                            className="slider-image"
                        />
                    </div>
                    <div className="image-indicator">
                        {look.images.map((_, index) => (
                            <span
                                key={index}
                                className={`dot ${currentImageIndex === index ? "active" : ""}`}
                            ></span>
                        ))}
                    </div>
                    <div style={{position: 'relative'}} className="product-single-info">
                        <h4>{look.name}</h4>
                        <p>{look.description}</p>
                    </div>
                </section>

                <div className={isModalOpen ? "chart-modal-overlay" : ""} onClick={handleOverlayClick}></div>
                <section className="look-products-list">
                    {look.products.map((product) => (
                        <div key={product.id} className="look-product-item">
                            <Link className="link" to={`/product/${product.id}`}>
                                <img src={product.main_image.link} alt={product.name} className="look-product-image"/>
                            </Link>
                            <div className="look-product-details">
                                <h3 className="look-product-brand">{product.brand.name}</h3>
                                <p className="look-product-name">{product.name}</p>
                                <p className="look-product-price">{formattedPrice(product.price, product.actual_price, product.sale)}</p>
                            </div>
                            <div className="look-product-actions">
                                <button style={{position: 'relative'}} className="product-list-favorite-icon" onClick={() => toggleSave(product)}>
                                    <span
                                        className="material-symbols-sharp"
                                        style={{
                                            fontVariationSettings: `'FILL' ${product.favourite?.id || savedItems[product.id] ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
                                            transition: "font-variation-settings 0.3s ease-in-out",
                                            cursor: "pointer",
                                        }}
                                    >
                                        bookmark
                                    </span>
                                </button>
                                <span
                                    onClick={() => {
                                        setActiveProduct(product);
                                        setAlertedItems();
                                        isMobile ? setIsModalOpen(true) : setDesktopModalOpen(true);
                                    }}
                                    className="material-symbols-sharp bag-icon"
                                >
                                    shopping_bag
                                </span>
                            </div>
                        </div>
                    ))}
                </section>

                {isModalOpen && activeProduct && (
                    <div className={`size-selector-modal ${isClosing ? "fade-out" : "fade-in"}`} onClick={closeModal}>
                        <div className="size-selector-container" onClick={(e) => e.stopPropagation()}>

                            <button className="size-chart-btn" onClick={fetchSizeChart}>
                                <span className="material-symbols-sharp">straighten</span> Size Chart
                            </button>

                            <div className="size-list">
                                {activeProduct.stocks.map((stock, index) => (
                                    <button 
                                        key={index}
                                        style={{background: stock.count === 0 ? '#ececec' : 'white', border: stock.count === 0 ? 'none': ''}}
                                        className={`size-button ${selectedSize === stock.size ? "active" : ""}`} 
                                        onClick={() => {
                                            if(stock.count === 0) {
                                                toggleRestock(stock.id, stock.size)
                                            } else {
                                                setSelectedSize(stock.size);
                                                handleAddProductToCart(activeProduct.id, stock.size);
                                                closeModal();
                                            }
                                        }}
                                    >
                                        {stock.size}
                                        
                                        {stock.count === 1 && (
                                            <span className="size-quantity">{stock.count} шт.</span>
                                        )}

                                        {stock.count === 0 && (
                                            <span
                                                className={`material-symbols-sharp alert-icon ${alertedStocks.has(stock.id) ? "filled" : "outlined"}`}
                                            >
                                                {alertedStocks.has(stock.id) ? "notifications_none" : "notifications"}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {desktopModalOpen && (
                    <div className="desktop-modal-overlay" onClick={closeDesktopModal}>
                        <div className="desktop-size-modal" onClick={e => e.stopPropagation()}>
                        <button
                            className="hamburger close"
                            onClick={closeDesktopModal}
                            aria-label="Toggle Modal"
                        >
                            <span className="material-symbols-sharp">
                                close
                            </span>
                        </button>
                            <h2>Выберите размер</h2>

                            <button className="desktop-size-chart-btn" onClick={fetchSizeChart}>
                                Таблица размеров
                            </button>

                            <div className="size-list">
                                {activeProduct?.stocks.map((stock, index) => (
                                    <button
                                        key={index}
                                        style={{
                                            background: stock.count === 0 ? '#ececec' : 'white',
                                            border: stock.count === 0 ? 'none' : '',
                                        }}
                                        className={`size-button ${selectedSize === stock.size ? 'active' : ''}`}
                                        onClick={() => {
                                            if (stock.count === 0) {
                                                toggleRestock(stock.id, stock.size);
                                            } else {
                                                setSelectedSize(stock.size);
                                                handleAddProductToCart(activeProduct.id, stock.size);
                                                closeModal();
                                            }
                                        }}
                                    >
                                        {stock.size}
                                        {stock.count === 1 && (
                                            <span className="size-quantity">{stock.count} шт.</span>
                                        )}
                                        {stock.count === 0 && (
                                            <span className={`material-symbols-sharp alert-icon ${alertedStocks.has(stock.id) ? 'filled' : 'outlined'}`}>
                                                {alertedStocks.has(stock.id) ? 'notifications_none' : 'notifications'}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {isSizeChartOpen && <SizeChartModal onSizeSelect={handleSizeSelect} tableData={sizeChart} closeChartModal={() => setIsSizeChartOpen(false)} />}
                {notification && (
                    <div className="notification">
                        {notification}
                    </div>
                )}
            </main>
        </div>
    );
}

export default Look;